/**
 * NotificationsTab - Sprint 19b
 *
 * Notification preferences tab in Settings.
 * @see US-19b.14
 */

import { useState, useEffect, useMemo } from 'react';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useResetNotificationPreferences,
} from '@/features/notifications/hooks/useNotificationPreferences';

import type { NotificationPreference, NotificationType } from '@bstock/shared';

/**
 * Group preferences by category
 */
function groupByCategory(
  preferences: NotificationPreference[]
): Record<string, NotificationPreference[]> {
  return preferences.reduce((acc, pref) => {
    if (!acc[pref.category]) {
      acc[pref.category] = [];
    }
    acc[pref.category].push(pref);
    return acc;
  }, {} as Record<string, NotificationPreference[]>);
}

/**
 * Toggle switch component
 */
function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`
        relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer
        rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        ${checked ? 'bg-primary-500' : 'bg-neutral-300'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-4 w-4
          transform rounded-full bg-white shadow ring-0
          transition duration-200 ease-in-out
          ${checked ? 'translate-x-4' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

/**
 * Preference row component
 */
function PreferenceRow({
  preference,
  inAppValue,
  emailValue,
  onInAppChange,
  onEmailChange,
}: {
  preference: NotificationPreference;
  inAppValue: boolean;
  emailValue: boolean;
  onInAppChange: (checked: boolean) => void;
  onEmailChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0">
      <span className="text-sm text-neutral-700">{preference.label}</span>
      <div className="flex items-center gap-6">
        <Toggle
          checked={inAppValue}
          onChange={onInAppChange}
          label={`In-App pour ${preference.label}`}
        />
        <Toggle
          checked={emailValue}
          onChange={onEmailChange}
          label={`Email pour ${preference.label}`}
        />
      </div>
    </div>
  );
}

/**
 * Category group component
 */
function PreferenceGroup({
  category,
  preferences,
  values,
  onChange,
}: {
  category: string;
  preferences: NotificationPreference[];
  values: Map<NotificationType, { inApp: boolean; email: boolean }>;
  onChange: (
    type: NotificationType,
    field: 'inApp' | 'email',
    value: boolean
  ) => void;
}) {
  return (
    <div className="mb-6">
      <h3 className="text-sm font-medium text-neutral-900 mb-2">{category}</h3>
      <div className="bg-white rounded-lg border border-neutral-200 px-4">
        {preferences.map((pref) => {
          const value = values.get(pref.type) ?? { inApp: pref.inApp, email: pref.email };
          return (
            <PreferenceRow
              key={pref.type}
              preference={pref}
              inAppValue={value.inApp}
              emailValue={value.email}
              onInAppChange={(checked) => onChange(pref.type, 'inApp', checked)}
              onEmailChange={(checked) => onChange(pref.type, 'email', checked)}
            />
          );
        })}
      </div>
    </div>
  );
}

export function NotificationsTab() {
  const { data, isLoading } = useNotificationPreferences();
  const updateMutation = useUpdateNotificationPreferences();
  const resetMutation = useResetNotificationPreferences();

  // Local state for modifications
  const [localValues, setLocalValues] = useState<
    Map<NotificationType, { inApp: boolean; email: boolean }>
  >(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local values when data loads
  useEffect(() => {
    if (data?.preferences) {
      const initial = new Map<NotificationType, { inApp: boolean; email: boolean }>();
      data.preferences.forEach((pref) => {
        initial.set(pref.type, { inApp: pref.inApp, email: pref.email });
      });
      setLocalValues(initial);
      setHasChanges(false);
    }
  }, [data]);

  // Group preferences by category
  const groupedPreferences = useMemo(() => {
    if (!data?.preferences) return {};
    return groupByCategory(data.preferences);
  }, [data]);

  // Handle value change
  const handleChange = (
    type: NotificationType,
    field: 'inApp' | 'email',
    value: boolean
  ) => {
    setLocalValues((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(type) ?? { inApp: true, email: true };
      newMap.set(type, { ...current, [field]: value });
      return newMap;
    });
    setHasChanges(true);
  };

  // Handle save
  const handleSave = () => {
    if (!data?.preferences) return;

    // Build preferences array from changes
    const preferences = data.preferences.map((pref) => {
      const value = localValues.get(pref.type) ?? { inApp: pref.inApp, email: pref.email };
      return {
        type: pref.type,
        inApp: value.inApp,
        email: value.email,
      };
    });

    updateMutation.mutate(
      { preferences },
      {
        onSuccess: () => {
          setHasChanges(false);
        },
      }
    );
  };

  // Handle reset
  const handleReset = () => {
    if (window.confirm('Reinitialiser toutes les preferences aux valeurs par defaut ?')) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-neutral-900">
          Preferences de notifications
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Choisissez comment recevoir vos notifications
        </p>
      </div>

      {/* Column headers */}
      <div className="flex items-center justify-end gap-6 mb-4 pr-4">
        <span className="text-xs font-medium text-neutral-500 w-9 text-center">
          In-App
        </span>
        <span className="text-xs font-medium text-neutral-500 w-9 text-center">
          Email
        </span>
      </div>

      {/* Preference groups */}
      {Object.entries(groupedPreferences).map(([category, preferences]) => (
        <PreferenceGroup
          key={category}
          category={category}
          preferences={preferences}
          values={localValues}
          onChange={handleChange}
        />
      ))}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-neutral-200">
        <Button
          variant="secondary"
          onClick={handleReset}
          disabled={resetMutation.isPending}
        >
          {resetMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RotateCcw className="w-4 h-4 mr-2" />
          )}
          Reinitialiser
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
        >
          {updateMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
